/*
 * The live sky blocks of the knowledge base: {{sky_now}}, {{sky_background}},
 * {{sky_speeds}}, {{sky_sun_theme}}, {{sky_retrograde}}, {{sky_moon_month}},
 * {{sky_rave_new_year}}, and the "Gate N in the sky" block on every gate page.
 *
 * The build writes each block as the sky stood at its own minute
 * (tools/build/docs_diagrams.py, "The live sky blocks"). This script downloads
 * the change list tools/build/build_sky.py publishes under /v/<release>/sky/,
 * replays it up to the reader's clock, and re-renders every block with the same
 * markup in the reader's own time zone, then again each minute. It never works
 * out a position: every gate, line and time is one the engine wrote into the
 * list, so the page and the app agree to the minute.
 *
 * When the list cannot answer (it did not load, or the reader's clock is
 * outside it) the build's text stays, with its "as of". An old time that says
 * so is better than a wrong one.
 *
 * Every body, gate and channel is a link. The build's links were moved onto
 * the page's locale by build_docs.localize_doc_links; the ones drawn here get
 * the same prefix, read off the block's hidden link to /docs/.
 *
 * No inline script and no second origin: the page loads /docs/i18n/sky-ref.js
 * (the gate, channel and body tables, generated from the engine) ahead of this,
 * and the {{mandala}} widget's renderer when the page has the wheel.
 */
(function () {
  "use strict";

  const T =
    window.GHD_DOCS_T ||
    function (key, fallback, vars) {
      const s = fallback == null ? key : fallback;
      return vars ? String(s).replace(/\{(\w+)\}/g, (m, k) => (vars[k] == null ? m : vars[k])) : s;
    };

  const MIN = 60000;
  const HOUR = 3600000;
  const DAY = 86400000;
  // Spelled in full so build_site's reference check holds both to a published
  // file; the months are built from the same directory at run time.
  const INDEX_URL = "/v/__GHD_VERSION__/sky/index.json";
  const LANDMARKS_URL = "/v/__GHD_VERSION__/sky/landmarks.json";
  const MONTH_DIR = INDEX_URL.slice(0, INDEX_URL.lastIndexOf("/") + 1);
  // The look-ahead slider's reach, and how far ahead each block reads on its
  // first render: the list's "to <gate> <when>" needs the next week, the Moon
  // strip and the gate pages' "the Moon arrives next" a whole lunar month.
  const AHEAD_HOURS = 168;
  const HORIZON = { now: 8 * DAY, moon: 28 * DAY, gate: 28 * DAY };
  const MOON_SPAN = 28 * DAY;
  // A block may ask for more months to find an end ("until" a channel breaks,
  // the Moon's next arrival), but never more than this far past the reader's
  // clock: past it, "until after <date>" is still true and costs nothing.
  const WANT_CAP = 95 * DAY;

  const NEVER_RETRO = { Sun: 1, Earth: 1, Moon: 1, "North Node": 1, "South Node": 1 };
  const MIRRORS = { Earth: 1, "South Node": 1 };
  // The planets whose turns the month files list (build_sky.STATION_BODIES).
  const STATION_BODIES = { Mercury: 1, Venus: 1, Mars: 1, Jupiter: 1, Saturn: 1, Uranus: 1, Neptune: 1, Pluto: 1 };
  // The wheel's geometry, as the engine's constants.py spells it.
  const GATE_START = 302;
  const GATE_DEG = 360 / 64;
  const LINE_DEG = GATE_DEG / 6;

  // --- The replay -----------------------------------------------------------
  //
  // One anchor per known state of a body: the start of each loaded month (with
  // the engine's measured direction and position inside the line) and every
  // change after it. A month owns (from, to]: its `start` already has any
  // change at `from` applied, and `minute` is rounded up, so replaying every
  // anchor at or before an instant gives the engine's state at that instant
  // (at most 59 s late, which no block shows).
  //
  // Direction comes from the engine too: `start` carries each body's flag, and
  // `stations` every minute a planet turns (build_sky.stations). A turn inside a
  // line crosses nothing, so a direction read off the steps between changes
  // would lag the engine until the next crossing; that reading is kept only as
  // the fallback for a file without stations, and for the Nodes' rare wobble.

  function Sky(ref, index) {
    this.ref = ref;
    this.bodies = index.bodies;
    this.from = Date.parse(index.from);
    this.until = Date.parse(index.until);
    this.months = index.months || [];
    this.files = {};
    this.landmarks = { sun: [], slow: [] };
    this.wanted = 0;
    this.idx = {};
    ref.wheel.forEach((gate, i) => {
      this.idx[gate] = i;
    });
    this.rebuild();
  }

  Sky.prototype.cell = function (gate, line) {
    return this.idx[gate] * 6 + (line - 1);
  };

  Sky.prototype.opposite = function (gate) {
    return this.ref.wheel[(this.idx[gate] + 32) % 64];
  };

  Sky.prototype.body = function (name) {
    return this.bodies.indexOf(name);
  };

  Sky.prototype.addMonth = function (key, file) {
    this.files[key] = file;
    this.rebuild();
  };

  Sky.prototype.addLandmarks = function (data) {
    this.landmarks = {
      sun: ((data && data.sun) || []).map((r) => ({ t: Date.parse(r[0]), g: r[1] })),
      slow: ((data && data.slow) || []).map((r) => ({ t: Date.parse(r[0]), b: r[1], g: r[2] })),
    };
  };

  // The loaded months that follow one another from the earliest, as anchors.
  // A gap ends the run: a replay across a missing month would be a guess.
  Sky.prototype.rebuild = function () {
    const run = [];
    for (let i = 0; i < this.months.length; i++) {
      const file = this.files[this.months[i]];
      if (file) run.push(this.months[i]);
      else if (run.length) break;
    }
    this.run = run;
    this.anchors = this.bodies.map(() => []);
    this.turns = this.bodies.map(() => []);
    this.changes = [];
    this.start = run.length ? Date.parse(this.files[run[0]].from) : NaN;
    this.end = run.length ? Date.parse(this.files[run[run.length - 1]].to) : NaN;
    run.forEach((key) => {
      const file = this.files[key];
      const from = Date.parse(file.from);
      file.start.forEach((row, b) => {
        const back = !!row[2];
        this.anchors[b].push({ t: from, g: row[0], l: row[1], retro: back, back: back, lp: row[3], start: true, b: b });
      });
      const stations = Array.isArray(file.stations) ? file.stations : null;
      const turned = {};
      (stations || []).forEach((s) => {
        const turn = { t: from + s[0] * MIN, retro: !!s[2] };
        this.turns[s[1]].push(turn);
        turned[s[1]] = true;
      });
      file.events.forEach((e) => {
        const list = this.anchors[e[0]];
        const prev = list[list.length - 1];
        // Which way the body stepped along the wheel, wrap-around aware: the
        // edge of the line it entered, and the direction when nothing better is
        // known.
        let step = this.cell(e[2], e[3]) - this.cell(prev.g, prev.l);
        if (step > 192) step -= 384;
        else if (step < -192) step += 384;
        const back = step === 0 ? prev.back : step < 0;
        const t = from + e[1] * MIN;
        // With stations, the flag is the month's start as turned since; the
        // Nodes (never listed) keep reading their steps.
        const known = stations && (this.bodies[e[0]] in STATION_BODIES || turned[e[0]]);
        const anchor = {
          t: t,
          g: e[2],
          l: e[3],
          retro: known ? this.turnedAt(e[0], t, prev) : back,
          back: back,
          start: false,
          b: e[0],
        };
        list.push(anchor);
        this.changes.push(anchor);
      });
    });
  };

  Sky.prototype.covers = function (t) {
    return this.run.length > 0 && t >= this.start && t <= this.end;
  };

  // Ask for the replay to reach `t`; boot() loads the months it takes.
  Sky.prototype.want = function (t) {
    if (t > this.wanted) this.wanted = t;
  };

  function lastAtOrBefore(list, t) {
    let lo = 0;
    let hi = list.length - 1;
    let found = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (list[mid].t <= t) {
        found = mid;
        lo = mid + 1;
      } else hi = mid - 1;
    }
    return found;
  }

  // A body's flag at `t` after the anchor `a`: the latest turn since, if any.
  Sky.prototype.turnedAt = function (b, t, a) {
    const turns = this.turns[b];
    const i = lastAtOrBefore(turns, t);
    return i >= 0 && turns[i].t >= a.t ? turns[i].retro : a.retro;
  };

  // A body's gate, line, direction and position inside the line at `t`. The
  // position runs between the anchors either side of `t`: a change in the
  // direction of travel enters its line at one edge and leaves at the other.
  Sky.prototype.state = function (b, t) {
    const list = this.anchors[b];
    const i = lastAtOrBefore(list, t);
    if (i < 0) return null;
    const a = list[i];
    const n = list[i + 1];
    // The position runs from the edge the body came in at to the edge it
    // leaves by; those are the steps' directions, whatever it did in between.
    let lp = a.start ? a.lp : a.back ? 1 : 0;
    if (n && n.t > a.t) {
      const end = n.start ? n.lp : n.back ? 0 : 1;
      lp += (end - lp) * ((t - a.t) / (n.t - a.t));
    }
    return { g: a.g, l: a.l, retro: this.turnedAt(b, t, a), lp: Math.min(1, Math.max(0, lp)) };
  };

  Sky.prototype.at = function (t) {
    return this.bodies.map((_, b) => this.state(b, t));
  };

  // Where the landmarks can answer for a body past the loaded months: every Sun
  // ingress (and so Earth's, opposite), and every gate change of the slow ones.
  Sky.prototype.landmarkNext = function (b, after, test) {
    const name = this.bodies[b];
    if (name === "Sun" || name === "Earth") {
      for (const r of this.landmarks.sun) {
        const gate = name === "Sun" ? r.g : this.opposite(r.g);
        if (r.t > after && test(gate)) return { t: r.t, g: gate };
      }
      return this.landmarks.sun.length ? null : undefined;
    }
    let listed = false;
    for (const r of this.landmarks.slow) {
      if (r.b !== b) continue;
      listed = true;
      if (r.t > after && test(r.g)) return { t: r.t, g: r.g };
    }
    return listed ? null : undefined;
  };

  // The first change after `t` whose gate passes `test` (given the gate before
  // it), from the loaded months, then the landmarks. null when neither knows;
  // then it asks for more months, for a body the landmarks do not follow.
  Sky.prototype.nextWhere = function (b, t, test) {
    const list = this.anchors[b];
    const i = lastAtOrBefore(list, t);
    for (let j = i + 1; j < list.length; j++) {
      if (!list[j].start && test(list[j].g, list[j - 1].g)) return { t: list[j].t, g: list[j].g };
    }
    const beyond = this.landmarkNext(b, Math.max(t, this.end), (g) => test(g, null));
    if (beyond === undefined && this.end < this.until) this.want(this.end + MIN);
    return beyond || null;
  };

  Sky.prototype.nextGate = function (b, t) {
    const now = this.state(b, t);
    return this.nextWhere(b, t, (g) => g !== now.g);
  };

  Sky.prototype.arrival = function (b, gate, t) {
    return this.nextWhere(b, t, (g, before) => g === gate && before !== gate);
  };

  // The channels the sky alone completes at `t`, each with the first instant
  // either gate is left empty: a walk over every later change, since a body
  // arriving can hold a gate after the one there leaves. `hints` are the
  // build's own answers, which stay exact for a channel unbroken since then.
  Sky.prototype.channels = function (t, hints) {
    const where = this.at(t).map((s) => s.g);
    const count = {};
    where.forEach((g) => {
      count[g] = (count[g] || 0) + 1;
    });
    const open = this.ref.channels.filter((c) => count[c[0]] && count[c[1]]).map((c) => ({ c: c, until: null }));
    let pending = open.length;
    for (let i = lastAtOrBefore(this.changes, t) + 1; i < this.changes.length && pending; i++) {
      const e = this.changes[i];
      const before = where[e.b];
      if (before === e.g) continue;
      count[before] -= 1;
      count[e.g] = (count[e.g] || 0) + 1;
      where[e.b] = e.g;
      open.forEach((o) => {
        if (o.until === null && (!count[o.c[0]] || !count[o.c[1]])) {
          o.until = e.t;
          pending -= 1;
        }
      });
    }
    open.forEach((o) => {
      if (o.until !== null) return;
      const hint = hints && hints[o.c[0] + "-" + o.c[1]];
      if (hint && hint > t) o.until = hint;
      else if (this.end < this.until) this.want(this.end + MIN);
    });
    return open;
  };

  // The Moon's gates from `t` for `span`, cut where the loaded months end.
  Sky.prototype.moonSegments = function (t, span) {
    const b = this.body("Moon");
    const list = this.anchors[b];
    const stop = Math.min(t + span, this.end);
    if (t + span > this.end) this.want(t + span);
    const out = [];
    let gate = this.state(b, t).g;
    let from = t;
    for (let j = lastAtOrBefore(list, t) + 1; j < list.length && list[j].t < stop; j++) {
      if (list[j].start || list[j].g === gate) continue;
      out.push({ g: gate, from: from, to: list[j].t });
      gate = list[j].g;
      from = list[j].t;
    }
    out.push({ g: gate, from: from, to: stop });
    return out;
  };

  Sky.prototype.sunIngresses = function (t, n) {
    return this.landmarks.sun.filter((r) => r.t > t).slice(0, n);
  };

  // Where a body sits on the wheel, in ecliptic degrees, for the mandala.
  function longitude(sky, s) {
    return (GATE_START + sky.idx[s.g] * GATE_DEG + (s.l - 1 + s.lp) * LINE_DEG) % 360;
  }

  // How far through its gate a body is, 0–1, in its direction of travel
  // (docs_diagrams.sky_travel).
  function travel(s) {
    const position = (s.l - 1 + s.lp) / 6;
    return s.retro ? 1 - position : position;
  }

  function monthKey(t) {
    return new Date(t).toISOString().slice(0, 7);
  }

  // --- Words, times and links ----------------------------------------------

  function esc(s) {
    return String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);
  }

  // A catalog string with markup in its placeholders: the text escaped, the parts not.
  function fill(key, fallback, parts) {
    return esc(T(key, fallback)).replace(/\{(\w+)\}/g, (m, k) => (parts[k] == null ? m : parts[k]));
  }

  function iso(t) {
    return new Date(t).toISOString().replace(/\.\d{3}Z$/, "Z");
  }

  // `timeZone` is left out in a browser, which is the reader's own; the tests
  // pin it.
  function View(options) {
    const lang = options.lang || undefined;
    const zone = options.timeZone ? { timeZone: options.timeZone } : {};
    const dtf = (o) => new Intl.DateTimeFormat(lang, Object.assign({}, o, zone));
    this.ref = options.ref;
    this.prefix = options.prefix || "";
    this.lang = lang;
    // The gate picked on the Moon stave, or 0; kept across the minute's re-render.
    this.pick = 0;
    this.fDay = dtf({ weekday: "short", hour: "2-digit", minute: "2-digit" });
    this.fDate = dtf({ day: "numeric", month: "short" });
    this.fDateYear = dtf({ day: "numeric", month: "short", year: "numeric" });
    this.fFull = dtf({ day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
    this.fYear = dtf({ year: "numeric" });
    this.rtf =
      typeof Intl.RelativeTimeFormat === "function" ? new Intl.RelativeTimeFormat(lang, { numeric: "always" }) : null;
    this.plural = typeof Intl.PluralRules === "function" ? new Intl.PluralRules(lang) : null;
    this.zone = options.timeZone || dtf({}).resolvedOptions().timeZone || "UTC";
    // The wall clock in the reader's zone, for finding its midnights.
    this.fWall = new Intl.DateTimeFormat(
      "en-US",
      Object.assign(
        {
          hourCycle: "h23",
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          second: "numeric",
        },
        zone,
      ),
    );
  }

  // A moment close by gets its weekday and clock; a later one its date, and
  // its year when that is not this one.
  View.prototype.when = function (t, from) {
    const d = t - from;
    if (d > -DAY && d < 6 * DAY) return this.fDay.format(t);
    return this.fYear.format(t) === this.fYear.format(from) ? this.fDate.format(t) : this.fDateYear.format(t);
  };

  View.prototype.time = function (t, from, dateOnly) {
    const text = dateOnly ? this.date(t, from) : this.when(t, from);
    return '<time datetime="' + iso(t) + '">' + esc(text) + "</time>";
  };

  View.prototype.date = function (t, from) {
    return this.fYear.format(t) === this.fYear.format(from) ? this.fDate.format(t) : this.fDateYear.format(t);
  };

  View.prototype.rel = function (t, from) {
    const m = Math.max(1, Math.round((t - from) / MIN));
    const [n, unit] =
      m < 60 ? [m, "minute"] : m < 48 * 60 ? [Math.round(m / 60), "hour"] : [Math.round(m / 1440), "day"];
    if (this.rtf) return this.rtf.format(n, unit);
    return "+" + n + " " + unit + (n === 1 ? "" : "s");
  };

  View.prototype.days = function (n) {
    const cat = this.plural ? this.plural.select(n) : n === 1 ? "one" : "other";
    const num = '<span class="hd-sky-num">' + n + "</span>";
    const key = cat === "one" ? "sky.days_one" : "sky.days_other";
    const fallback = cat === "one" ? "{n} day" : "{n} days";
    // A locale may carry its own few/many forms; English falls back to other.
    const own = cat !== "one" && cat !== "other" ? T("sky.days_" + cat, null) : null;
    if (own && own !== "sky.days_" + cat) return esc(own).replace("{n}", num);
    return fill(key, fallback, { n: num });
  };

  // The reader's wall clock at `t`, as [year, month, day, hour, minute, second].
  View.prototype.wall = function (t) {
    const parts = {};
    this.fWall.formatToParts(t).forEach((p) => {
      parts[p.type] = Number(p.value);
    });
    return [parts.year, parts.month, parts.day, parts.hour % 24, parts.minute, parts.second];
  };

  // The first instant of a calendar day in the reader's zone. Usually that is
  // 00:00, but where the clocks jump forward AT midnight (America/Santiago,
  // 6 September 2026) there is no 00:00 and the day starts at 01:00 — and an
  // offset read at a guess can then alternate between the two sides of the
  // jump instead of settling (Codex review, round 3). So the offset only
  // brackets the answer; the first minute whose local date is `day` is found
  // by bisection, three hours either side, which covers every zone's jump.
  View.prototype.midnight = function (year, month, day) {
    const want = Date.UTC(year, month - 1, day);
    const dayOf = (t) => {
      const w = this.wall(t);
      return Date.UTC(w[0], w[1] - 1, w[2]);
    };
    const w = this.wall(want);
    const offset = Date.UTC(w[0], w[1] - 1, w[2], w[3], w[4], w[5]) - Math.floor(want / 1000) * 1000;
    let lo = want - offset - 3 * 3600000;
    let hi = want - offset + 3 * 3600000;
    while (hi - lo > MIN) {
      const mid = lo + Math.floor((hi - lo) / 2 / MIN) * MIN;
      if (dayOf(mid) >= want) hi = mid;
      else lo = mid;
    }
    return hi;
  };

  // Every local midnight after `from` and before `to`, a calendar day apart:
  // 23 or 25 hours across a clock change, never a fixed 24.
  View.prototype.midnights = function (from, to) {
    const w = this.wall(from);
    const out = [];
    for (let k = 1; ; k++) {
      const day = new Date(Date.UTC(w[0], w[1] - 1, w[2] + k));
      const t = this.midnight(day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate());
      if (t >= to) return out;
      if (t > from) out.push(t);
    }
  };

  View.prototype.href = function (path) {
    return this.prefix + path;
  };

  View.prototype.gate = function (g, text, cls) {
    const klass = cls ? ' class="' + cls + '"' : "";
    return "<a" + klass + ' href="' + this.href(this.ref.gates[g][1]) + '">' + esc(text == null ? g : text) + "</a>";
  };

  View.prototype.planetName = function (name) {
    return T("chart.planet." + name.toLowerCase().replace(/ /g, ""), name);
  };

  View.prototype.planet = function (name, cls, symbol) {
    const row = this.ref.bodies.find((r) => r[0] === name);
    const glyph = symbol === false ? "" : '<span class="hd-sky-sym" aria-hidden="true">' + row[2] + "</span> ";
    return (
      '<a class="' +
      (cls || "hd-sky-who") +
      '" href="' +
      this.href(row[1]) +
      '">' +
      glyph +
      esc(this.planetName(name)) +
      "</a>"
    );
  };

  View.prototype.center = function (key) {
    return T(
      "center." + key,
      key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    );
  };

  View.prototype.rx = function () {
    return (
      ' <abbr class="hd-sky-rx" title="' +
      esc(T("sky.retrograde", "Retrograde")) +
      '">' +
      esc(T("sky.retrograde_short", "R")) +
      "</abbr>"
    );
  };

  // --- The blocks -----------------------------------------------------------
  //
  // Each returns the markup for its block's [data-sky-body], the same shape
  // docs_diagrams writes. `t` is the instant shown; `sky.end` is where the
  // loaded months stop, the date an unanswered "until" is still true past.

  function untilText(view, sky, t, found, key, fallback) {
    if (found) return fill(key || "sky.until", fallback || "until {when}", { when: view.time(found.t || found, t) });
    return fill("sky.until_after", "until after {when}", { when: view.time(sky.end, t, true) });
  }

  function nowList(view, sky, t) {
    const rows = sky.at(t).map((s, b) => {
      const name = sky.bodies[b];
      const next = sky.nextGate(b, t);
      const until = next
        ? fill("sky.to_gate", "to {gate} {when}", { gate: view.gate(next.g), when: view.time(next.t, t) })
        : fill("sky.stays_past", "same gate past {when}", { when: view.time(sky.end, t, true) });
      const rx = s.retro && !NEVER_RETRO[name] ? view.rx() : "";
      const sun = name === "Sun" || name === "Earth" ? " is-sun" : "";
      return (
        '<li class="hd-sky-b"><span class="hd-sky-who-cell">' +
        view.planet(name) +
        rx +
        "</span>" +
        view.gate(s.g, s.g + "." + s.l, "hd-sky-gl" + sun) +
        view.gate(s.g, view.ref.gates[s.g][0], "hd-sky-gn") +
        '<span class="hd-sky-until">' +
        until +
        "</span></li>"
      );
    });
    return '<ol class="hd-sky-bodies">' + rows.join("") + "</ol>";
  }

  function background(view, sky, t, hints) {
    const now = sky.at(t);
    const rows = sky.channels(t, hints).map((o) => {
      const [a, b, name, url] = o.c;
      const holders = now
        .map((s, i) =>
          s.g === a || s.g === b
            ? fill("sky.in_gate", "{planet} in {gate}", {
                planet: view.planet(sky.bodies[i], "hd-sky-p", false),
                gate: view.gate(s.g),
              })
            : null,
        )
        .filter(Boolean)
        .join(", ");
      const centers = esc(
        T("sky.centers", "{a} to {b}", { a: view.center(view.ref.gates[a][2]), b: view.center(view.ref.gates[b][2]) }),
      );
      return (
        '<li data-sky-channel="' +
        a +
        "-" +
        b +
        '"><a class="hd-sky-pill" href="' +
        view.href(url) +
        '">' +
        a +
        "-" +
        b +
        '</a><span class="hd-sky-t"><a class="hd-sky-name" href="' +
        view.href(url) +
        '">' +
        esc(name) +
        '</a> <span class="hd-sky-sub">· ' +
        holders +
        " · " +
        centers +
        '</span></span><span class="hd-sky-u">' +
        untilText(view, sky, t, o.until) +
        "</span></li>"
      );
    });
    if (!rows.length)
      return (
        '<p class="hd-sky-note">' +
        esc(T("sky.background.none", "No channel is complete in the sky right now.")) +
        "</p>"
      );
    return '<ul class="hd-sky-rows hd-sky-chan">' + rows.join("") + "</ul>";
  }

  function speeds(view, sky, t) {
    const rows = [];
    sky.at(t).forEach((s, b) => {
      const name = sky.bodies[b];
      if (MIRRORS[name]) return;
      const next = sky.nextGate(b, t);
      const nextText = next
        ? fill("sky.next_gate_at", "next gate {when}", { when: esc(view.rel(next.t, t)) })
        : fill("sky.stays_past", "same gate past {when}", { when: view.time(sky.end, t, true) });
      const width = Math.max(3, Math.min(100, travel(s) * 100)).toFixed(1);
      rows.push(
        '<li><span class="hd-sky-who-cell">' +
          view.planet(name) +
          (s.retro && !NEVER_RETRO[name] ? view.rx() : "") +
          '</span><span class="hd-sky-bar"><i class="' +
          (s.retro ? "is-rev" : "") +
          '" style="width:' +
          width +
          '%"></i></span><span class="hd-sky-r">' +
          view.gate(s.g, s.g + "." + s.l) +
          " · " +
          nextText +
          "</span></li>",
      );
    });
    const note = esc(
      T(
        "sky.speeds.note",
        "Bars fill in the direction of travel. A body marked R is retrograde: seen from Earth, it moves backwards through the wheel.",
      ),
    );
    return '<ul class="hd-sky-rows hd-sky-speeds">' + rows.join("") + '</ul><p class="hd-sky-note">' + note + "</p>";
  }

  function bigGate(view, s, name) {
    return (
      '<div class="hd-sky-big">' +
      view.planet(name, "hd-sky-eyebrow") +
      '<a class="hd-sky-num is-sun" href="' +
      view.href(view.ref.gates[s.g][1]) +
      '">' +
      s.g +
      "<small>." +
      s.l +
      "</small></a>" +
      view.gate(s.g, view.ref.gates[s.g][0], "hd-sky-gn") +
      "</div>"
    );
  }

  function sunTheme(view, sky, t) {
    const sunB = sky.body("Sun");
    const sun = sky.state(sunB, t);
    const earth = sky.state(sky.body("Earth"), t);
    const next = sky.nextGate(sunB, t);
    const moves = next
      ? '<p class="hd-sky-note">' +
        fill("sky.sun.moves", "The Sun moves to gate {gate} on {when}.", {
          gate: view.gate(next.g),
          when: view.time(next.t, t),
        }) +
        "</p>"
      : "";
    const rows = sky
      .sunIngresses(t, 4)
      .map(
        (r) =>
          "<li>" +
          view.gate(r.g, T("sky.gate_label", "Gate {gate}", { gate: r.g }) + " · " + view.ref.gates[r.g][0]) +
          view.time(r.t, t, true) +
          "</li>",
      );
    return (
      '<div class="hd-sky-pair">' +
      bigGate(view, sun, "Sun") +
      bigGate(view, earth, "Earth") +
      "</div>" +
      moves +
      '<p class="hd-sky-sublabel">' +
      esc(T("sky.sun.next", "The Sun's next gates")) +
      '</p><ol class="hd-sky-rows hd-sky-dates">' +
      rows.join("") +
      "</ol>"
    );
  }

  function retrograde(view, sky, t) {
    const planets = sky
      .at(t)
      .map((s, b) => ({ name: sky.bodies[b], retro: s.retro }))
      .filter((p) => !NEVER_RETRO[p.name]);
    const n = planets.filter((p) => p.retro).length;
    const chips = planets.map((p) => {
      const row = view.ref.bodies.find((r) => r[0] === p.name);
      return (
        '<li><a class="hd-sky-chip' +
        (p.retro ? " is-rx" : "") +
        '" href="' +
        view.href(row[1]) +
        '"><span class="hd-sky-sym" aria-hidden="true">' +
        row[2] +
        "</span> " +
        esc(view.planetName(p.name)) +
        (p.retro ? " · " + esc(T("sky.retrograde_short", "R")) : "") +
        "</a></li>"
      );
    });
    const vars = { n: n, total: planets.length };
    const count =
      n === 0
        ? T("sky.retro.count_none", "None of the {total} planets is retrograde.", vars)
        : n === 1
          ? T("sky.retro.count_one", "1 of the {total} planets is retrograde.", vars)
          : T("sky.retro.count_many", "{n} of the {total} planets are retrograde.", vars);
    const note = T("sky.retro.note", "The Nodes always move backwards, and the Sun, Earth and Moon never do.");
    return (
      '<ul class="hd-sky-chips">' + chips.join("") + '</ul><p class="hd-sky-note">' + esc(count + " " + note) + "</p>"
    );
  }

  function pct(ms, from) {
    return Math.max(0, Math.min(100, ((ms - from) / MOON_SPAN) * 100)).toFixed(3);
  }

  // The stave under the Moon strip: one row per center, in bodygraph order.
  // STAVE_ROW is the row height in px and must match sky.css (.hd-sky-stave).
  const STAVE_CENTERS = ["head", "ajna", "throat", "g", "heart", "solar_plexus", "spleen", "sacral", "root"];
  const STAVE_ROW = 18;

  // The gates that form a channel with `gate`, from the engine's channel table.
  function partners(ref, gate) {
    const out = [];
    ref.channels.forEach((c) => {
      if (c[0] === gate) out.push(c[1]);
      else if (c[1] === gate) out.push(c[0]);
    });
    return out;
  }

  function listOf(view, items) {
    if (typeof Intl.ListFormat === "function") {
      try {
        return new Intl.ListFormat(view.lang, { type: "conjunction" }).format(items);
      } catch (e) {
        // an unknown locale tag: fall through to the plain join
      }
    }
    return items.join(", ");
  }

  // The line under the stave: the hint, or what the picked gate pairs with.
  function staveNote(view, pick) {
    if (!pick) {
      return esc(
        T(
          "sky.moon.stave_hint",
          "Each gate sits in the row of its center. Tap a gate to see the gates it forms a channel with.",
        ),
      );
    }
    const gates = view.ref.gates;
    return fill("sky.moon.stave_pick", "Gate {gate} ({name}, {center}) forms a channel with {partners}.", {
      gate: view.gate(pick),
      name: esc(gates[pick][0]),
      center: esc(view.center(gates[pick][2])),
      partners: listOf(
        view,
        partners(view.ref, pick).map((g) => view.gate(g, T("sky.gate_label", "Gate {gate}", { gate: g }))),
      ),
    });
  }

  function stave(view, sky, t) {
    const pick = view.pick || 0;
    const lit = {};
    if (pick) {
      lit[pick] = "is-pick";
      partners(view.ref, pick).forEach((g) => {
        lit[g] = "is-on";
      });
    }
    const height = STAVE_CENTERS.length * STAVE_ROW;
    const rowOf = (g) => STAVE_CENTERS.indexOf(view.ref.gates[g][2]);
    const mid = (g) => rowOf(g) * STAVE_ROW + STAVE_ROW / 2;
    const names = STAVE_CENTERS.map((c) => "<span>" + esc(view.center(c)) + "</span>");
    const lines = STAVE_CENTERS.map(
      (c, i) => '<span class="hd-sky-stave-line" style="top:' + (i * STAVE_ROW + STAVE_ROW / 2) + 'px"></span>',
    );
    const points = [];
    const blocks = sky.moonSegments(t, MOON_SPAN).map((seg, i) => {
      const left = pct(seg.from, t);
      const right = pct(seg.to, t);
      const y = mid(seg.g);
      points.push(left + "," + y, right + "," + y);
      const label = esc(
        T("sky.gate_label", "Gate {gate}", { gate: seg.g }) +
          " · " +
          view.ref.gates[seg.g][0] +
          " · " +
          view.center(view.ref.gates[seg.g][2]),
      );
      const state = pick ? " " + (lit[seg.g] || "is-dim") : "";
      return (
        '<a class="hd-sky-sg' +
        (i === 0 ? " is-cur" : "") +
        state +
        '" data-sky-g="' +
        seg.g +
        '" href="' +
        view.href(view.ref.gates[seg.g][1]) +
        '" title="' +
        label +
        '" style="left:' +
        left +
        "%;width:" +
        (right - left).toFixed(3) +
        "%;top:" +
        (y - 6) +
        'px">' +
        seg.g +
        "</a>"
      );
    });
    const ticks = view
      .midnights(t, t + MOON_SPAN)
      .map((d) => '<span class="hd-sky-stave-tick" style="left:' + pct(d, t) + '%"></span>');
    const aria = esc(T("sky.moon.stave_aria", "The Moon's gates over the next 28 days, by center"));
    return {
      names: '<div class="hd-sky-stave-names" aria-hidden="true"><i></i>' + names.join("") + "</div>",
      body:
        '<div class="hd-sky-stave" role="group" aria-label="' +
        aria +
        '" style="height:' +
        height +
        'px">' +
        lines.join("") +
        ticks.join("") +
        '<svg class="hd-sky-stave-path" viewBox="0 0 100 ' +
        height +
        '" preserveAspectRatio="none" aria-hidden="true" focusable="false"><polyline points="' +
        points.join(" ") +
        '"/></svg>' +
        blocks.join("") +
        "</div>",
    };
  }

  function moonMonth(view, sky, t) {
    const b = sky.body("Moon");
    const moon = sky.state(b, t);
    const next = sky.nextGate(b, t);
    const line = fill(
      "sky.moon.line",
      "The Moon is in gate {gate} ({name}) until {when}. It passes through all 64 gates in about 27.3 days: a Reflector's full cycle.",
      {
        gate: view.gate(moon.g),
        name: view.gate(moon.g, view.ref.gates[moon.g][0]),
        when: next ? view.time(next.t, t) : "",
      },
    );
    const parts = sky.moonSegments(t, MOON_SPAN).map((seg, i) => {
      const left = pct(seg.from, t);
      const width = (pct(seg.to, t) - left).toFixed(3);
      const label = esc(T("sky.gate_label", "Gate {gate}", { gate: seg.g }) + " · " + view.ref.gates[seg.g][0]);
      return (
        '<a class="hd-sky-seg' +
        (i === 0 ? " is-cur" : "") +
        '" href="' +
        view.href(view.ref.gates[seg.g][1]) +
        '" title="' +
        label +
        '" style="left:' +
        left +
        "%;width:" +
        width +
        '%">' +
        seg.g +
        "</a>"
      );
    });
    // Ticks at the reader's own midnights, a date on every third.
    view.midnights(t, t + MOON_SPAN).forEach((d, i) => {
      parts.push('<span class="hd-sky-tick" style="left:' + pct(d, t) + '%"></span>');
      if (i % 3 === 0)
        parts.push('<span class="hd-sky-day" style="left:' + pct(d, t) + '%">' + esc(view.fDate.format(d)) + "</span>");
    });
    parts.push('<span class="hd-sky-nowline"></span>');
    const aria = esc(T("sky.moon.strip_aria", "The Moon's gates over the next 28 days"));
    const rows = stave(view, sky, t);
    return (
      '<p class="hd-sky-note">' +
      line +
      '</p><div class="hd-sky-moonfig">' +
      rows.names +
      '<div class="hd-sky-strip-wrap" role="region" tabindex="0" aria-label="' +
      aria +
      '"><div class="hd-sky-strip">' +
      parts.join("") +
      "</div>" +
      rows.body +
      '</div></div><p class="hd-sky-note hd-sky-stave-note" data-sky-stave-note>' +
      staveNote(view, view.pick || 0) +
      "</p>"
    );
  }

  function raveNewYear(view, sky, t) {
    const next = sky.landmarks.sun.find((r) => r.g === 41 && r.t > t);
    if (!next) {
      return (
        '<p class="hd-sky-note">' +
        esc(T("sky.rny.none", "The Sun's next entry into gate 41 is further ahead than this page can see.")) +
        "</p>"
      );
    }
    const days = Math.ceil((next.t - t) / DAY);
    return (
      '<p class="hd-sky-count">' +
      view.days(days) +
      '</p><p class="hd-sky-note">' +
      fill("sky.rny.when", "The Sun enters gate {gate} on {when}. That starts the Rave year.", {
        gate: view.gate(41),
        when: '<time datetime="' + iso(next.t) + '">' + esc(view.fFull.format(next.t)) + "</time>",
      }) +
      "</p>"
    );
  }

  function gatePage(view, sky, t, gate) {
    const lead =
      '<p class="hd-sky-lead">' +
      esc(
        T("sky.gate.lead", "Every body passes through Gate {gate} in turn. This is where the sky stands with it now.", {
          gate: gate,
        }),
      ) +
      "</p>";
    const rows = [];
    const here = {};
    sky.at(t).forEach((s, b) => {
      if (s.g !== gate) return;
      const name = sky.bodies[b];
      here[name] = true;
      const text = fill("sky.gate.here", "{planet} is here now, line {line}", {
        planet: view.planet(name, "hd-sky-p", false),
        line: String(s.l),
      });
      rows.push("<li><span>" + text + "</span><span>" + untilText(view, sky, t, sky.nextGate(b, t)) + "</span></li>");
    });
    if (!rows.length)
      rows.push(
        "<li><span>" +
          esc(T("sky.gate.nobody", "No body is in Gate {gate} right now.", { gate: gate })) +
          "</span><span></span></li>",
      );
    ["Moon", "Sun"].forEach((name) => {
      if (here[name]) return;
      const found = sky.arrival(sky.body(name), gate, t);
      if (!found) return;
      const text = fill("sky.gate.arrives", "{planet} arrives next", {
        planet: view.planet(name, "hd-sky-p", false),
      });
      rows.push("<li><span>" + text + "</span>" + view.time(found.t, t, name === "Sun") + "</li>");
    });
    return lead + '<ul class="hd-sky-rows hd-sky-dates">' + rows.join("") + "</ul>";
  }

  const RENDER = {
    background: background,
    speeds: speeds,
    sun: sunTheme,
    retrograde: retrograde,
    moon: moonMonth,
    rny: raveNewYear,
  };

  // --- On the page ------------------------------------------------------------

  // "/nl" from the block's hidden, build-localized link to "/nl/docs/"; "" in English.
  function localePrefix(block) {
    const home = block.querySelector(".hd-sky-home");
    const href = (home && home.getAttribute("href")) || "/docs/";
    return href.replace(/\/docs\/?$/, "");
  }

  function stampLive(stamp, view, preview, t) {
    if (!stamp) return;
    stamp.className = "hd-sky-stamp " + (preview ? "is-preview" : "is-live");
    stamp.textContent = preview
      ? T("sky.preview", "Preview · {when}", { when: view.fDay.format(t) })
      : T("sky.live_zone", "Live · {zone}", { zone: view.zone.replace(/_/g, " ") });
  }

  // The build's "until" for each background channel, read before the first
  // render replaces it.
  function channelHints(block) {
    const hints = {};
    block.querySelectorAll("[data-sky-channel]").forEach((li) => {
      const t = Date.parse(li.getAttribute("data-sky-until") || "");
      if (!Number.isNaN(t)) hints[li.getAttribute("data-sky-channel")] = t;
    });
    return hints;
  }

  function planetsForWheel(sky, t) {
    return sky
      .at(t)
      .map((s, b) => ({ id: sky.bodies[b].replace(/ /g, ""), gate: s.g, line: s.l, lon: longitude(sky, s) }));
  }

  // The {{sky_now}} block: the mandala's wheel, the list, and the look-ahead.
  function NowBlock(block, view, doc) {
    this.block = block;
    this.view = view;
    this.offset = 0;
    const body = block.querySelector("[data-sky-body]");
    body.textContent = "";
    const grid = doc.createElement("div");
    grid.className = "hd-sky-now";
    this.wheelHost = doc.createElement("div");
    this.wheelHost.className = "hd-sky-wheel";
    this.list = doc.createElement("div");
    this.list.className = "hd-sky-list";
    grid.appendChild(this.wheelHost);
    grid.appendChild(this.list);
    body.appendChild(grid);
    const mandala = window.GHD_MANDALA;
    this.wheel = mandala && typeof mandala.mount === "function" ? mandala.mount(this.wheelHost, { bare: true }) : null;
    if (!this.wheel) this.wheelHost.hidden = true;

    const id = "hd-sky-ahead-" + Math.random().toString(36).slice(2, 8);
    const scrub = doc.createElement("div");
    scrub.className = "hd-sky-scrub";
    const label = doc.createElement("label");
    label.setAttribute("for", id);
    label.textContent = T("sky.look_ahead", "Look ahead");
    this.range = doc.createElement("input");
    this.range.type = "range";
    this.range.id = id;
    this.range.min = "0";
    this.range.max = String(AHEAD_HOURS);
    this.range.step = "1";
    this.range.value = "0";
    this.out = doc.createElement("output");
    this.out.setAttribute("for", id);
    this.out.textContent = T("sky.now_value", "now");
    const reset = doc.createElement("button");
    reset.type = "button";
    reset.textContent = T("sky.now_button", "Now");
    scrub.appendChild(label);
    scrub.appendChild(this.range);
    scrub.appendChild(this.out);
    scrub.appendChild(reset);
    block.appendChild(scrub);
    this.onChange = null;
    this.range.addEventListener("input", () => {
      this.offset = Number(this.range.value) || 0;
      if (this.onChange) this.onChange();
    });
    reset.addEventListener("click", () => {
      this.range.value = "0";
      this.offset = 0;
      if (this.onChange) this.onChange();
    });
  }

  NowBlock.prototype.render = function (sky, now) {
    const t = now + this.offset * HOUR;
    this.list.innerHTML = nowList(this.view, sky, t);
    if (this.wheel) this.wheel.setPlanets(planetsForWheel(sky, t));
    if (this.offset) {
      const h = this.offset % 24;
      const d = (this.offset - h) / 24;
      const offset = (d ? d + " d " : "") + h + " h";
      this.out.textContent = T("sky.ahead_value", "{when} (+{offset})", {
        when: this.view.fDay.format(t),
        offset: offset,
      });
    } else this.out.textContent = T("sky.now_value", "now");
    stampLive(this.block.querySelector("[data-sky-stamp]"), this.view, !!this.offset, t);
  };

  function Block(block, view) {
    this.block = block;
    this.view = view;
    this.kind = block.getAttribute("data-sky");
    this.gate = Number(block.getAttribute("data-sky-gate")) || 0;
    this.hints = this.kind === "background" ? channelHints(block) : null;
    this.body = block.querySelector("[data-sky-body]");
    if (this.kind === "moon" && block.addEventListener) {
      // A plain tap on a stave gate picks it instead of following the link; a
      // modified click still opens the gate page, and so does the note's link.
      block.addEventListener("click", (e) => {
        const a = e.target && e.target.closest ? e.target.closest(".hd-sky-sg") : null;
        if (!a || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        const g = Number(a.getAttribute("data-sky-g"));
        this.view.pick = this.view.pick === g ? 0 : g;
        if (this.last) this.render(this.last.sky, this.last.now);
      });
    }
  }

  Block.prototype.render = function (sky, now) {
    const html =
      this.kind === "gate"
        ? gatePage(this.view, sky, now, this.gate)
        : RENDER[this.kind](this.view, sky, now, this.hints);
    // The strip scrolls sideways on a phone; a re-render must not throw the
    // reader back to its start.
    const wrap = this.body.querySelector && this.body.querySelector(".hd-sky-strip-wrap");
    const scrolled = wrap ? wrap.scrollLeft : 0;
    this.body.innerHTML = html;
    if (scrolled) {
      const again = this.body.querySelector(".hd-sky-strip-wrap");
      if (again) again.scrollLeft = scrolled;
    }
    this.last = { sky: sky, now: now };
    stampLive(this.block.querySelector("[data-sky-stamp]"), this.view, false, now);
  };

  // A build-time block the list cannot bring up to date keeps its text; only
  // its times move into the reader's zone, which is the same instant said
  // closer to home.
  function localizeTimes(blocks, view) {
    blocks.forEach((block) => {
      block.querySelectorAll("time[datetime]").forEach((el) => {
        const t = Date.parse(el.getAttribute("datetime"));
        if (Number.isNaN(t)) return;
        el.textContent = el.hasAttribute("data-sky-date") ? view.fDateYear.format(t) : view.fFull.format(t);
      });
    });
  }

  function getJSON(url) {
    return fetch(url, { credentials: "same-origin" }).then((r) => {
      if (!r.ok) throw new Error(url + " answered " + r.status);
      return r.json();
    });
  }

  // Run the page's blocks. `env` stands in for the browser in the tests:
  // { document, fetch → getJSON, now(), setInterval, timeZone }.
  function boot(env) {
    env = env || {};
    const doc = env.document || document;
    const clock = env.now || Date.now;
    const load = env.getJSON || getJSON;
    const every = env.setInterval || setInterval;
    const ref = env.ref || window.GHD_SKY_REF;
    const elements = Array.prototype.slice.call(doc.querySelectorAll("[data-sky]"));
    if (!elements.length || !ref) return Promise.resolve(null);
    const pageLang =
      (doc.documentElement && doc.documentElement.getAttribute && doc.documentElement.getAttribute("lang")) ||
      undefined;
    // Gregorian dates on a Thai page too (GHD.i18n.intlTag); Thai defaults to the Buddhist era.
    const i18n = window.GHD && window.GHD.i18n;
    const lang = pageLang && i18n && i18n.intlTag ? i18n.intlTag(pageLang) : pageLang;
    const views = elements.map(
      (el) => new View({ ref: ref, prefix: localePrefix(el), lang: lang, timeZone: env.timeZone }),
    );

    let sky = null;
    let blocks = null;
    let loading = null;

    function keep() {
      localizeTimes(elements, views[0]);
      return null;
    }

    function loadMonth(key) {
      if (!key || sky.files[key]) return Promise.resolve();
      return load(MONTH_DIR + key + ".json").then((file) => sky.addMonth(key, file));
    }

    // The months up to `t`, one after another from the last one loaded.
    function reach(t) {
      const last = sky.run[sky.run.length - 1];
      const next = sky.months[sky.months.indexOf(last) + 1];
      if (!(t > sky.end) || !next) return Promise.resolve();
      return loadMonth(next).then(() => reach(t));
    }

    let shown = 0;

    // The list has run out under an open page: every block keeps the last
    // state it drew, and says when that was.
    function stop() {
      blocks.forEach((b, i) => {
        const stamp = b.block.querySelector("[data-sky-stamp]");
        if (!stamp) return;
        stamp.className = "hd-sky-stamp is-asof";
        stamp.textContent = T("sky.as_of", "As of {when}", { when: views[i].fFull.format(shown) });
      });
    }

    function render() {
      const now = clock();
      if (now > sky.until) {
        if (shown) stop();
        return false;
      }
      shown = now;
      sky.wanted = 0;
      blocks.forEach((b) => b.render(sky, now));
      const want = Math.min(sky.wanted, now + WANT_CAP, sky.until);
      if (want > sky.end && !loading) {
        const before = sky.end;
        loading = reach(want)
          .catch(() => {})
          .then(() => {
            loading = null;
            // Only a longer list can answer more; a failed or final fetch
            // leaves the "until after" text standing.
            if (sky.end > before) render();
          });
      }
      return true;
    }

    return load(INDEX_URL)
      .then((index) => {
        const now = clock();
        sky = new Sky(ref, index);
        if (!(now >= sky.from && now < sky.until) || sky.months.indexOf(monthKey(now)) < 0) return keep();
        let horizon = 8 * DAY;
        elements.forEach((el) => {
          horizon = Math.max(horizon, HORIZON[el.getAttribute("data-sky")] || 0);
        });
        return Promise.all([load(LANDMARKS_URL).then((d) => sky.addLandmarks(d)), loadMonth(monthKey(now))])
          .then(() => reach(Math.min(now + horizon, sky.until)))
          .then(() => {
            if (!sky.covers(now)) return keep();
            blocks = elements.map((el, i) =>
              el.getAttribute("data-sky") === "now" ? new NowBlock(el, views[i], doc) : new Block(el, views[i]),
            );
            blocks.forEach((b) => {
              if (b instanceof NowBlock) b.onChange = render;
            });
            render();
            // Keep "now" honest while the page stays open; a closed list stops
            // at its last state rather than guess past its end.
            const cancel = env.clearInterval || clearInterval;
            const timer = every(() => {
              if (!render()) cancel(timer);
            }, MIN);
            return sky;
          });
      })
      .catch(keep);
  }

  window.GHD_SKY = {
    Sky: Sky,
    View: View,
    render: {
      now: nowList,
      background: background,
      speeds: speeds,
      sun: sunTheme,
      retrograde: retrograde,
      moon: moonMonth,
      rny: raveNewYear,
      gate: gatePage,
    },
    longitude: longitude,
    travel: travel,
    monthKey: monthKey,
    localePrefix: localePrefix,
    partners: partners,
    staveCenters: STAVE_CENTERS,
    boot: boot,
  };

  if (window.GHD_SKY_NO_BOOT) return;
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => boot());
  else boot();
})();
